import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Loader2, Sparkles, Upload, X } from "lucide-react";
import { MAX_SMART_UPLOAD, UploadItem } from "@/schema/smartupload.schema";

interface UploadViewProps {
  items: UploadItem[];
  isUploading: boolean;
  onAddFiles: (files: FileList | null) => void;
  onRemoveItem: (previewUrl: string) => void;
  onClearAll: () => void;
  onStartAISorting: () => void;
}

export function UploadView({
  items,
  isUploading,
  onAddFiles,
  onRemoveItem,
  onClearAll,
  onStartAISorting,
}: UploadViewProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  return (
    <>
      {/* Header */}
      <div className="flex items-center px-8 pt-7 pb-5 shrink-0">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Sparkles className="h-4 w-4 text-purple-500" />
          Smart Product Upload
        </h2>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col min-h-0 px-8 overflow-y-auto">
        {/* Drop zone */}
        <div
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            onAddFiles(e.dataTransfer.files);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onClick={() => inputRef.current?.click()}
          className={cn(
            "flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed cursor-pointer transition-colors shrink-0",
            items.length > 0 ? "h-[150px]" : "flex-1 min-h-[320px]",
            isDragging
              ? "border-primary bg-primary/5"
              : "border-gray-200 hover:border-gray-300 bg-gray-50/50",
          )}
        >
          <Upload className="h-10 w-10 text-gray-400" />
          <div className="text-center">
            <p className="text-sm font-medium text-gray-700">
              Drag &amp; drop product photos here
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              or click to browse (max {MAX_SMART_UPLOAD} images)
            </p>
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => onAddFiles(e.target.files)}
        />

        {/* Selected images grid */}
        {items.length > 0 && (
          <div className="mt-5 pb-2">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-purple-600">
                {items.length} image{items.length !== 1 ? "s" : ""} selected
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClearAll}
                className="text-muted-foreground hover:text-foreground"
              >
                Clear all
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {items.map(({ previewUrl }, i) => (
                <div
                  key={previewUrl}
                  className="relative aspect-square rounded-lg overflow-hidden bg-gray-100"
                >
                  <img
                    src={previewUrl}
                    alt={`upload-${i}`}
                    className="w-full h-full object-cover"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveItem(previewUrl);
                    }}
                    className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/50 text-white hover:bg-black/70 hover:text-white"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-end px-8 py-5 border-t border-gray-100 shrink-0">
        <Button
          type="button"
          disabled={items.length === 0 || isUploading}
          onClick={onStartAISorting}
          className="rounded-full px-6 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white gap-2"
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          {isUploading ? "Uploading..." : "Start AI Sorting"}
        </Button>
      </div>
    </>
  );
}
